import React, { useState, useEffect } from 'react';
import './CreateAccountBroker.scss';

import { StepBarContent } from '../StepBar/StepBar';
import { FooterContent } from '../ResumeFooter/ResumeFooter';
import { OptionDropdownInner } from '../../atoms/OptionDropdown/OptionDropdown';
import { TextInput } from '../../atoms/TextInput/TextInput';
import { AddressCaption } from '../PreApprovalForm/PreApprovalForm';
import { AddressCaptionInner } from '../PreApprovalForm/PreApprovalForm';
import { InputAutocompleteAddress } from '../../atoms/TextInput/TextInput';

import { formatPhoneNumber } from '../PreApprovalForm/PreApprovalForm';
import { isProduction } from '../../../utils/constants';

const options = [
  {
    value: "client",
    label: "My client",
    disabled: false,
    className: undefined
  }
]

export const CreateAccountBroker = (props) => {
  /* Props */
  const { onSubmission, values } = props;

  /* User Data */
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState('')
  const [passwordVerification, setPasswordVerification] = useState('')

  const [borrowerFirstName, setBorrowerFirstName] = useState("")
  const [borrowerLastName, setBorrowerLastName] = useState("")
  const [investmentAddress, setInvestmentAddress] = useState("")

  const createOnChange = (fun) => (e) => fun(e.target.value);

  /* State - Step */
  const [step, setStep] = useState(0)

  const firstStepCompleted = (
    !isProduction ?
      true && firstName && lastName && email && phoneNumber && password && passwordVerification
      :
      true && firstName && lastName && email && phoneNumber
  )
  

  const setSelectedStep = (newStep) => {
    if (step === 0 && !firstStepCompleted) {
      return
    } else {
      setStep(newStep)
    }
  }

  const nextStep = () => {
    setSelectedStep(step + 1)
  }

  /* Markup */
  const firstStepOnClick = () => {
    nextStep()
  }

  const stepBarMarkup = (
    <StepBarContent
      selectedStep={step}
      setSelectedStep={setSelectedStep}
      options={["Broker Information", "Borrower Information"]}
    />
  )

  const [errors, setErrors] = useState({});

  const brokerData = {
    firstName,
    lastName,
    email,
    phoneNumber,
    ...(
      !isProduction ?
      {
        password,
        passwordVerification
      }
      :
      { }
    )
  }

  const isFormBrokerComplete = () => {
    return Object.values(brokerData).every(value => value !== undefined && value !== '');
  }

  const validateInput = (label, value) => {
    switch (label) {
      case "First name":
      case "Last name":
        return value.trim() === "" ? "This field is required" : null;
      case "Email address":
        const emailRegex = /\S+@\S+\.\S+/;
        return !emailRegex.test(value) ? "Invalid email address" : null;
      case "Phone number":
        return value.trim() === "" ? "This field is required" : null;
      case "Password":
        return value.length < 8 ? "Must be at least 8 characters" : null;
      case "Confirm Password":
        return !password || !value || value !== password ? "Passwords do not match" : null;
      default:
        return null;
    }
  };

  const checkErrors = () => {
    const newErrors = {
      firstName: validateInput("First name", firstName),
      lastName: validateInput("Last name", lastName),
      email: validateInput("Email address", email),
      phoneNumber: validateInput("Phone number", phoneNumber),
      ...(
        !isProduction ?
        {
          password: validateInput("Password", password),
          passwordConfirmation: validateInput("Confirm Password", passwordVerification)
        }
        :
        { }
      )
    };
    setErrors(newErrors);
    return newErrors
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors();
    }
  }, [firstName, lastName, email, phoneNumber, password, passwordVerification, investmentAddress])

  const handleContinue = () => {
    const errors = checkErrors();
    const hasErrors = Object.values(errors).some(error => error !== null);
    if (isFormBrokerComplete() && !hasErrors) {
      nextStep()
    }
  }

  const firstStepMarkup = (
    <React.Fragment>
      <div className='inner-container'>
        { stepBarMarkup }    
        <h1 className='title'>
          Enter your info to offer loans to your real estate investor clients
        </h1>

        <div className='wide-input-container'>
          <TextInput label={"First name"} value={firstName} onChange={(e) => { setFirstName(e) }} error={errors.firstName} />
          <TextInput label={"Last name"} value={lastName} onChange={(e) => setLastName(e)} error={errors.lastName} />
          <TextInput label={"Email address"} value={email} onChange={(e) => setEmail(e)} error={errors.email} />
          <TextInput label={"Phone number"} value={phoneNumber} onChange={(e) => setPhoneNumber(formatPhoneNumber(e))} error={errors.phoneNumber} />
          {
            !isProduction ?
              <div className='password-container'>
                <TextInput isPassword label={"Password"} value={password} onChange={(e) => setPassword(e)} error={errors.password} />
                <TextInput isPassword label={"Confirm Password"} value={passwordVerification} onChange={(e) => setPasswordVerification(e)} error={errors.passwordConfirmation} />
              </div>
            :
              <></>
          }
        </div>
      </div>

      <div className='resume-container'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerMode={true} isBrokerResume={true} />
      </div>

      <div className='loan-options-button' onClick={handleContinue}>
        Go to borrower info
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  const secondStepOnClick = () => {
    const userData = {
      broker: {
        firstName, lastName, email, phoneNumber, password
      },
      borrower: {
        firstName: borrowerFirstName,
        lastName: borrowerLastName,
        investmentAddress: investmentAddress
      }
    }
    onSubmission(userData)
  }

  const secondStepMarkup = (
    <React.Fragment>
      <div className='padding-container'>
        { stepBarMarkup }
        <h1 className='title'>
          Enter borrower info to get a term sheet and pre-approval letter
        </h1>

        <p className='description'>
          <OptionDropdownInner
            defaultValue={options[0]}
            options={options}
            customWidth={"200px"}
            isBrokerMode={true}
          /> is the borrower
        </p>

        <div className='input-container'>
          <TextInput label='First Name' value={borrowerFirstName} onChange={(e) => setBorrowerFirstName(e)} />
          <TextInput label='Last Name' value={borrowerLastName} onChange={(e) => setBorrowerLastName(e)} />
        </div>
      </div>

      <div className='resume-container first'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerFirst={true} />
      </div>

      <div className='padding-container'>
        <div className='property'>
          <InputAutocompleteAddress
            className='broker'
            onSelect={({ formatted_address }) => {
              const addressWithoutCountry = formatted_address.replace(/, [^,]*$/, "");
              setInvestmentAddress(addressWithoutCountry);
            }}
          />
        </div>
        <AddressCaption />
      </div>

      <div className='resume-container last'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerLast={true} />
      </div>

      <div className='loan-options-button' onClick={secondStepOnClick}>
        Get a term sheet for your client
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  /* Component */
  return (
    <div className='create-account-broker broker'>
      <div className='info'>
        {
          step === 0
            ?
            firstStepMarkup
            :
            secondStepMarkup
        }
      </div>
    </div>
  );
}
